/**
 * AXIOS CONFIGURATION
 * HTTP client with interceptors for auth, error handling, and logging
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TIMEOUT, ERROR_CODES, STORAGE_KEYS } from '@/utils/constants';
import { ApiResponse } from '@/types/api.types';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracing
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    if (config.headers) {
      config.headers['X-Request-ID'] = requestId;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh token
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (refreshToken) {
          // The gateway mounts auth under /api/v2 only (the old v1 auth paths were 404s). The refresh
          // answers {accessToken, token, refreshToken}: the refresh token rotates, so keep the new one.
          const response = await axios.post(`${API_BASE_URL}/api/v2/auth/refresh`, {
            refreshToken,
          });

          const accessToken: string = response.data?.accessToken || response.data?.token;
          if (!accessToken) throw new Error('refresh returned no access token');
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
          if (response.data?.refreshToken) {
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
          }

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - redirect to login
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const apiError = normalizeError(error);
    console.error('[API Response Error]', apiError);
    return Promise.reject(apiError);
  }
);

/**
 * Normalize error to standard format
 */
function normalizeError(error: AxiosError): ApiResponse<null> {
  if (error.response) {
    // Server responded with error status
    const data = error.response.data as any;
    return {
      success: false,
      error: {
        code: data?.error?.code || getErrorCodeFromStatus(error.response.status),
        message: data?.error?.message || error.message,
        details: data?.error?.details,
      },
      timestamp: Date.now(),
      requestId: error.config?.headers?.['X-Request-ID'] as string || '',
    };
  } else if (error.request) {
    // Request made but no response
    return {
      success: false,
      error: {
        code: ERROR_CODES.NETWORK_ERROR,
        message: 'Network error - no response from server',
      },
      timestamp: Date.now(),
      requestId: error.config?.headers?.['X-Request-ID'] as string || '',
    };
  } else {
    // Something else happened
    return {
      success: false,
      error: {
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error.message,
      },
      timestamp: Date.now(),
      requestId: '',
    };
  }
}

/**
 * Get error code from HTTP status
 */
function getErrorCodeFromStatus(status: number): string {
  switch (status) {
    case 401:
      return ERROR_CODES.UNAUTHORIZED;
    case 403:
      return ERROR_CODES.FORBIDDEN;
    case 404:
      return ERROR_CODES.NOT_FOUND;
    case 400:
      return ERROR_CODES.VALIDATION_ERROR;
    case 503:
      return ERROR_CODES.SERVICE_UNAVAILABLE;
    case 504:
      return ERROR_CODES.TIMEOUT;
    default:
      return ERROR_CODES.INTERNAL_ERROR;
  }
}

export default axiosInstance;
